import { useI18n } from "../../i18n";
import React, { useCallback, useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Stacks } from "./portfolioContent";
import styles from "./styles/portfolioModal.module.css";
import { preloadPortfolioImage } from "./preloadPortfolioImage";

function GalleryImage({ image, onReady }) {
  const { t } = useI18n();
  const imageRef = useRef(null);
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    const node = imageRef.current;
    if (node.complete)
      setStatus(node.naturalWidth > 0 ? "loaded" : "failed");
  }, []);

  useEffect(() => {
    if (status === "loaded") onReady();
  }, [status, onReady]);

  if (status === "failed") return <p role="alert">{t("imageError")}</p>;
  return (
    <>
      {status === "loading" && (
        <div className={styles.loading} role="status">
          <span className={styles.spinner} aria-hidden="true" />
          {t("imageLoading")}
        </div>
      )}
      <img
        ref={imageRef}
        src={image.src}
        alt={image.alt}
        className={status === "loading" ? styles.pending : undefined}
        draggable={false}
        decoding="async"
        onLoad={() => setStatus("loaded")}
        onError={() => setStatus("failed")}
      />
    </>
  );
}

export default function PortfolioModal({ item, onClose }) {
  const { t } = useI18n();
  const dialogRef = useRef(null);
  const closeRef = useRef(null);
  const titleId = useId();
  const [index, setIndex] = useState(0);
  const images = item.images;
  const preloadNext = useCallback(() => {
    if (images.length > 1)
      preloadPortfolioImage(images[(index + 1) % images.length].src);
  }, [images, index]);
  const changeImage = (delta) => {
    setIndex((current) => (current + delta + images.length) % images.length);
  };

  useEffect(() => {
    const dialog = dialogRef.current;
    const trigger = document.activeElement;
    const overflow = document.body.style.overflow;
    dialog.showModal();
    closeRef.current.focus();
    document.body.style.overflow = "hidden";
    return () => {
      dialog.close();
      document.body.style.overflow = overflow;
      if (trigger?.isConnected) trigger.focus({ preventScroll: true });
    };
  }, []);

  return createPortal(
    <dialog
      ref={dialogRef}
      className={styles.dialog}
      aria-labelledby={titleId}
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
      onClick={(event) => {
        if (event.target !== event.currentTarget) return;
        const rect = event.currentTarget.getBoundingClientRect();
        if (
          event.clientX < rect.left ||
          event.clientX > rect.right ||
          event.clientY < rect.top ||
          event.clientY > rect.bottom
        )
          onClose();
      }}
      onKeyDown={(event) => {
        if (
          images.length > 1 &&
          ["ArrowLeft", "ArrowRight"].includes(event.key)
        ) {
          event.preventDefault();
          changeImage(event.key === "ArrowLeft" ? -1 : 1);
        }
      }}
    >
      <div className={styles.content}>
        <header className={styles.header}>
          <div>
            <h2 id={titleId}>{item.title}</h2>
            <p className={styles.date}>{item.date}</p>
          </div>
          <button
            ref={closeRef}
            type="button"
            className={styles.close}
            onClick={onClose}
            aria-label={t("close")}
          >
            ×
          </button>
        </header>
        <div className={styles.body}>
          <div className={styles.gallery}>
            <GalleryImage
              key={images[index].src}
              image={images[index]}
              onReady={preloadNext}
            />
            {images.length > 1 && (
              <>
                <button
                  type="button"
                  className={`${styles.arrow} ${styles.previous}`}
                  aria-label={t("previous")}
                  onClick={() => changeImage(-1)}
                >
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                    focusable="false"
                  >
                    <path d="M15 6 9 12 15 18" />
                  </svg>
                </button>
                <button
                  type="button"
                  className={`${styles.arrow} ${styles.next}`}
                  aria-label={t("next")}
                  onClick={() => changeImage(1)}
                >
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                    focusable="false"
                  >
                    <path d="m9 6 6 6-6 6" />
                  </svg>
                </button>
              </>
            )}
          </div>
          <div className={styles.caption}>
            <span aria-live="polite" aria-atomic="true">
              {index + 1} / {images.length} · {images[index].caption}
            </span>
            {item.imageSource && (
              <a
                href={item.imageSource.url}
                target="_blank"
                rel="noopener noreferrer"
              >
                {t("source")}: {item.imageSource.label}
              </a>
            )}
          </div>
          <div className={styles.detail}>
            {item.detailDescription.split(/\n\s*\n/).map((paragraph) => (
              <p key={paragraph}>{paragraph}</p>
            ))}
            {item.detailTopics?.length > 0 && (
              <ul className={styles.topics}>
                {item.detailTopics.map((topic) => (
                  <li key={topic.id}>
                    <strong>
                      {topic.title}
                      {topic.date && ` (${topic.date})`}:
                    </strong>{" "}
                    {topic.description}
                  </li>
                ))}
              </ul>
            )}
          </div>
          {item.stacks?.length > 0 && (
            <div className={styles.stacks}>
              <Stacks stacks={item.stacks} />
            </div>
          )}
          {item.link && (
            <a
              className={styles.link}
              href={item.link}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t("more")} ↗
            </a>
          )}
        </div>
      </div>
    </dialog>,
    document.body,
  );
}
